export namespace ApiTypes {
  export type SearchParams = { [key in string]: string | number | boolean | null | undefined };

  export type Options = {
    /**
     * Treat `url` as absolute and send it untouched — no API root, no `Authorization`
     * header, no case conversion. For third-party URLs the API hands us, chiefly the
     * pre-signed S3 `PUT` from `/uploads/presign`.
     */
    raw?: boolean;
    headers?: { [key in string]: string };
    searchParams?: SearchParams;
    /** Defaults to `json`. `empty` is for endpoints that answer `204 No Content`. */
    responseType?: 'json' | 'blob' | 'empty';
    /** Opt a call out of the automatic error snackbar; the observable still errors. */
    silent?: boolean;
    /**
     * Skip snake_case ↔ camelCase conversion on this call's body and response. Only for
     * payloads whose keys are data rather than field names.
     */
    skipCaseConversion?: boolean;
  };

  /**
   * The one shape every failure from this layer arrives in, so no feature has to
   * interpret a raw `Response`. `status` is `0` when the request never reached the
   * server — a DNS failure, an offline browser, CORS.
   */
  export type Error = {
    status: number;
    message: string;
    /** FastAPI's `detail`, when the error body carried one. */
    detail?: unknown;
    /**
     * The API's machine-readable `code`, for the few errors a client has to tell apart from
     * others sharing a status — see `WIKI_EMPTY_CODE`.
     */
    code?: string;
  };

  /**
   * The chat stream's wire contract (TDR §5.6). Note that an `error` frame never reaches
   * a subscriber as a value — `$stream` converts it into an observable error, because a
   * failure that arrives as a `next` is a failure every caller has to remember to check.
   * It is part of this union because it is part of the contract being parsed.
   */
  export type StreamEvent =
    | { type: 'delta'; text: string }
    | {
        type: 'done';
        success: boolean;
        error?: string;
        citedPaths: string[];
        citations: { sourceKey: string; filename: string }[];
      }
    | { type: 'error'; message: string };

  export type UploadEvent = { type: 'progress'; loaded: number; total: number } | { type: 'done' };
}

/**
 * The `code` of the `409` the API answers a chat message with while there is no wiki yet
 * (CE-14530). A `409` alone is ambiguous — the gardening single-flight is one too — so
 * this is what tells "there is no knowledge base yet" apart from any other conflict.
 */
export const WIKI_EMPTY_CODE = 'wiki_empty';

/**
 * The `code`s of the `409` a plain gardening trigger is refused with when it would change
 * nothing: the wiki is already up to date with its sources, or there are no sources to
 * build one from. A forced trigger (`?force=true`) skips both. A run already in flight is
 * also a `409`, but carries no code.
 */
export const WIKI_UP_TO_DATE_CODE = 'wiki_up_to_date';
export const NO_SOURCES_CODE = 'no_sources';

/** Narrows an unknown rejection to a normalised API error. */
export const isApiError = (err: unknown): err is ApiTypes.Error =>
  typeof err === 'object' &&
  err !== null &&
  typeof (err as ApiTypes.Error).status === 'number' &&
  typeof (err as ApiTypes.Error).message === 'string';

/**
 * Whether a failure is "there is no wiki yet" — expected on a fresh environment, and shown
 * as an empty state rather than as an error (see `handleFailure`).
 */
export const isWikiEmptyError = (err: unknown): boolean =>
  isApiError(err) && err.status === 409 && err.code === WIKI_EMPTY_CODE;

/** Whether a gardening trigger was refused for having nothing to do — see `WIKI_UP_TO_DATE_CODE`. */
export const isNothingToGardenError = (err: unknown): boolean =>
  isApiError(err) &&
  err.status === 409 &&
  (err.code === WIKI_UP_TO_DATE_CODE || err.code === NO_SOURCES_CODE);
